import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Filter } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

import { planningAPI } from "../services/planningService";
import { employeesAPI } from "../services/api";
import ApprovalStatsCards from "./schedule/ApprovalStatsCards";
import ApprovalPendingAlert from "./schedule/ApprovalPendingAlert";
import ApprovalOverrideList from "./schedule/ApprovalOverrideList";
import ApprovalDialog from "./schedule/ApprovalDialog";

const ScheduleOverrideApproval = () => {
  const { t } = useTranslation();
  const [overrides, setOverrides] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [selectedOverride, setSelectedOverride] = useState(null);
  const [approvalAction, setApprovalAction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [summary, setSummary] = useState(null);

  useEffect(() => { loadData(); }, [statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadOverrides(), loadEmployees(), loadSchedules(), loadSummary()]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadOverrides = async () => {
    try {
      const filters = statusFilter !== "ALL" ? { status: statusFilter } : {};
      const data = await planningAPI.overrides.getScheduleOverrides(filters);
      setOverrides(data.items || []);
    } catch (error) {
    }
  };

  const loadEmployees = async () => {
    try {
      const data = await employeesAPI.getEmployees({ limit: 1000 });
      setEmployees(data.employees || []);
    } catch (error) {
    }
  };

  const loadSchedules = async () => {
    try {
      const data = await planningAPI.schedules.getSchedules({ limit: 100 });
      setSchedules(data.items || []);
    } catch (error) {
    }
  };

  const loadSummary = async () => {
    try {
      const data = await planningAPI.overrides.getOverridesSummary();
      setSummary(data);
    } catch (error) {
    }
  };

  const handleOpenApprovalDialog = (override, action) => {
    setSelectedOverride(override);
    setApprovalAction(action);
    setRejectionReason("");
    setShowApprovalDialog(true);
  };

  const handleApproveOrReject = async () => {
    try {
      if (approvalAction === "reject" && !rejectionReason.trim()) {
        toast.error(t("planning.legacy.approvals.errors.rejectionRequired"));
        return;
      }
      await planningAPI.overrides.approveOrRejectOverride(selectedOverride.id, {
        approved: approvalAction === "approve",
        rejection_reason: approvalAction === "reject" ? rejectionReason : null,
      });
      await loadData();
      setShowApprovalDialog(false);
      setSelectedOverride(null);
      setApprovalAction(null);
      setRejectionReason("");
    } catch (error) {
    }
  };

  const pendingCount = overrides.filter((o) => o.status === "pending").length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-muted-foreground">{t("planning.legacy.approvals.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("planning.legacy.approvals.title")}</h3>
          <p className="text-sm text-muted-foreground">
            {t("planning.legacy.approvals.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">{t("planning.legacy.approvals.filters.all")}</SelectItem>
              <SelectItem value="pending">{t("planning.overridesPanel.statuses.pending")}</SelectItem>
              <SelectItem value="approved">{t("planning.overridesPanel.statuses.approved")}</SelectItem>
              <SelectItem value="rejected">{t("planning.overridesPanel.statuses.rejected")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <ApprovalStatsCards summary={summary} />

      {pendingCount > 0 && statusFilter !== "pending" && (
        <ApprovalPendingAlert
          pendingCount={pendingCount}
          onViewPending={() => setStatusFilter("pending")}
        />
      )}

      <ApprovalOverrideList
        overrides={overrides}
        employees={employees}
        schedules={schedules}
        statusFilter={statusFilter}
        handleOpenApprovalDialog={handleOpenApprovalDialog}
      />

      <ApprovalDialog
        open={showApprovalDialog}
        onOpenChange={setShowApprovalDialog}
        approvalAction={approvalAction}
        selectedOverride={selectedOverride}
        employees={employees}
        schedules={schedules}
        rejectionReason={rejectionReason}
        setRejectionReason={setRejectionReason}
        handleApproveOrReject={handleApproveOrReject}
      />
    </div>
  );
};

export default ScheduleOverrideApproval;
